import React, { Fragment, useCallback, useEffect, useState } from 'react';
import moment from 'moment-timezone';

import { useOrgsData, useOrgsMapFromOrgs } from 'app/dataProviders/org/OrgsDataProvider';
import { PlainInputDate } from 'app/common/ui-next/plain-input-date';
import PlainLabel from 'app/common/ui-next/plain-label';
import PlainHelperText from 'app/common/ui-next/plain-helper-text';
import OrgsAndGroupsSelector from 'app/components/common/OrgsAndGroupsSelector';

import useOrgsSelector from './useOrgsSelector';
import type { Errors } from './errors';
import useDateFrom from './useDateFrom';
import { useEditGeneralSettings } from 'app/dataProviders/generalSettings/hooks';
import styles from './CopyKitFormOtherOrgs.module.scss'
import MenuBuildingSelector from './menuTargetSelectors/MenuBuildingSelector';

export const dateFormat = 'YYYY-MM-DD';

type Props = {
  orgIdFrom: number,
  buildingId?: number,
  initialDateTo: string,
  disabled: boolean,
  onChangeOrgsSelected: (ids: Array<string>) => void,
  onChangeBuildingSelected: (buildingId: string) => void,
  onChangeDateTo: (date: string) => void,
  errors: Errors,
  resetErrors: () => void,
}

const CopyKitFormOtherOrgs = ({
  orgIdFrom: orgId,
  buildingId,
  initialDateTo,
  disabled,
  onChangeOrgsSelected,
  onChangeBuildingSelected,
  onChangeDateTo,
  errors,
  resetErrors,
}: Props) => {
  const { generalSettings } = useEditGeneralSettings()


  const [dateValue, setDateValue] = useState(initialDateTo);

  const handleDateChange = useCallback((newDate: string) => {
    setDateValue(newDate);
    onChangeDateTo(newDate);
    resetErrors();
  }, [onChangeDateTo, resetErrors]);

  const [{ orgs: orgsState, loading: loadingOrgs, currentOrg }] = useOrgsData();

  const actualFromDate = useDateFrom(orgsState, { orgId });
  const { type } = currentOrg;

  const orgsMap = useOrgsMapFromOrgs(orgsState);

  const orgID = String(orgId);
  const ignoreCurrent = useCallback(({ ID }: { ID: string }) => {
    return orgID !== ID;
  }, [orgID]);

  const [selectorProps] = useOrgsSelector(orgsState, { date: dateValue, orgId });
  const { selection: orgsSelected, selection, defaultSelection, onChange } = selectorProps;

  const disabledDays = useCallback((dayFormatted: string, format: string, unit?: string) => {
    const normalizedUnit = (unit ?? 'day') as moment.unitOfTime.StartOf;

    return actualFromDate
      ? !moment(dayFormatted, format).isAfter(actualFromDate, normalizedUnit)
      : false;
  }, [actualFromDate]);

  useEffect(() => {
    onChangeOrgsSelected(orgsSelected);
  }, [orgsSelected]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <Fragment>
      {generalSettings.useComplexBuildingVariants &&
        <MenuBuildingSelector
          orgId={orgId}
          defaultBuildingId={buildingId}
          direction='from'
          disabled={disabled}
          onChange={onChangeBuildingSelected}
        />}
      <PlainLabel htmlFor="date_to">
        {'На дату'}
      </PlainLabel>

      <PlainInputDate
        id="date_to"
        value={dateValue}
        onDateChange={handleDateChange}
        dateFormat={dateFormat}
        disabled={disabled}
        loading={loadingOrgs}
        dateDisabledStatus={disabledDays}
        error={Boolean(errors.date)}
        calendarCloseOnClick
        displayAsBlock
      />

      <PlainHelperText overflowVisible error>
        {errors.date}
      </PlainHelperText>

      <OrgsAndGroupsSelector
        orgType={type}
        label="В заведения"
        header="Выберите куда копировать комплект"
        pleaseSelectText="Выберите заведение"
        disabled={disabled || loadingOrgs}
        orgs={orgsMap}
        multiple
        selectedAllOnEmptySelection={false}
        selection={selection}
        defaultSelection={defaultSelection}
        onChange={onChange}
        filterFunc={ignoreCurrent}
      />

      {generalSettings.useComplexBuildingVariants &&
        <div className={styles.warning}>{'Копирование комплектов во все корпуса'}</div>}

      {errors.org
        && <PlainHelperText overflowVisible error>
          {errors.org}
        </PlainHelperText>}
    </Fragment>
  );
};

export default CopyKitFormOtherOrgs;
